import {
    AccountIbanOption,
    AccountOwnerNameOption,
    BankTransactionConversionRate,
    BankTransactionDetails,
    BankTransactionListItem,
    BankTransactionTotalsDto,
    BankTransactionUpdateDto,
    BankTransactionUpdateSummary,
    EntryDebtorIdOption,
    EntryDebtorNameOption,
} from 'types/bank-transfers.ts';
import { objectToQueryParams } from 'utils/linkUtil';
import { APIInvoicesSlice } from './invoices.ts';
import { portalAPI } from './portalAPI.ts';
import { accumulatedQueryWithFilters } from './util/endpointBuilder.ts';

const APIBankTransfersSlice = portalAPI
    .enhanceEndpoints({ addTagTypes: ['BankTransfers', 'BankTransfersCount', 'BankTransfersTotals'] })
    .injectEndpoints({
        endpoints: (builder) => ({
            uploadBankTransfers: builder.mutation<BankTransactionUpdateSummary, FileList>({
                query: (files: FileList) => {
                    const formData = new FormData();
                    Array.from(files).forEach((file) => {
                        formData.append('files', file);
                    });
                    return {
                        url: '/v1/bank-transactions/upload',
                        method: 'POST',
                        body: formData,
                        formData: true,
                    };
                },
                async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIBankTransfersSlice.util.invalidateTags(['BankTransfers', 'BankTransfersTotals']));
                    dispatch(APIInvoicesSlice.util.invalidateTags(['Notifications']));
                },
            }),
            updateBankTransfer: builder.mutation<BankTransactionDetails, BankTransactionUpdateDto>({
                query: (dto) => ({
                    url: `/v1/bank-transactions/${dto.id}`,
                    method: 'PUT',
                    body: dto,
                }),
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIInvoicesSlice.util.invalidateTags(['Notifications']));
                },
            }),
            getBankTransfersWithFilters: accumulatedQueryWithFilters<BankTransactionListItem>(
                builder,
                '/v1/bank-transactions',
                {
                    providesTags: ['BankTransfers'],
                }
            ),
            getBankTransfersTotals: builder.query<BankTransactionTotalsDto, { filters?: string }>({
                query: ({ filters = '' }) =>
                    `/v1/bank-transactions/calculate-totals${objectToQueryParams({ filter: filters || undefined })}`,
                providesTags: ['BankTransfersTotals'],
                keepUnusedDataFor: 0,
            }),
            getBankTransferById: builder.query<BankTransactionDetails, string>({
                query: (id) => `/v1/bank-transactions/${id}`,
                keepUnusedDataFor: 0,
            }),
            getBankTransferConversionRate: builder.query<
                BankTransactionConversionRate,
                { id: number; invoiceId: number }
            >({
                query: ({ id, invoiceId }) =>
                    `/v1/bank-transactions/${id}/conversion-rate${objectToQueryParams({ invoiceId })}`,
            }),
            getBankTransferReceiverIbanOptions: accumulatedQueryWithFilters<AccountIbanOption>(
                builder,
                `/v1/bank-transactions/options/receiver-iban`
            ),
            getBankTransferReceiverNameOptions: accumulatedQueryWithFilters<AccountOwnerNameOption>(
                builder,
                `/v1/bank-transactions/options/receiver-name`
            ),
            getBankTransferSenderNameOptions: accumulatedQueryWithFilters<EntryDebtorNameOption>(
                builder,
                `/v1/bank-transactions/options/sender-name`
            ),
            getBankTransferSenderIdOptions: accumulatedQueryWithFilters<EntryDebtorIdOption>(
                builder,
                `/v1/bank-transactions/options/sender-id`
            ),
        }),
        overrideExisting: false,
    });

export const {
    useUpdateBankTransferMutation,
    useUploadBankTransfersMutation,
    useLazyGetBankTransfersWithFiltersQuery,
    useGetBankTransfersTotalsQuery,
    useLazyGetBankTransferReceiverIbanOptionsQuery,
    useLazyGetBankTransferReceiverNameOptionsQuery,
    useLazyGetBankTransferSenderNameOptionsQuery,
    useLazyGetBankTransferSenderIdOptionsQuery,
    useGetBankTransferByIdQuery,
    useLazyGetBankTransferConversionRateQuery,
} = APIBankTransfersSlice;
